import { ConstrainMode, DetailsListLayoutMode, DetailsRow, Dialog, DialogFooter, DialogType, FontIcon, IColumn, IDetailsListProps, IDialogContentProps, IDragDropContext, IStackItemStyles, IStackTokens, Icon, IconButton, Link, PrimaryButton, SelectionMode, ShimmeredDetailsList, Stack, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import React from "react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../src/taskpane/components/_v2/redux/storeMain";
import './ContentHome.css';
import { IPageSettings } from "../../src/taskpane/interfaces/IPageSettings";
import { FolderTypeIconEnum } from "../../src/taskpane/components/enums/FolderTypeIconEnum";
import { IconTypeNameResolver } from "../../src/taskpane/components/helpers/iconTypeNameResolver";
import { ComponentTypes } from "../../src/taskpane/config/config";
import { FolderTypeIconQuery } from "../../src/taskpane/components/helpers/folderTypeIconQuery";
import { ImageTypeEnum } from "../../src/taskpane/components/enums/ImageTypeEnum";
import { FileIconType, getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import { ImageSizeEnum } from "../../src/taskpane/components/enums/ImageSizeEnum";
import { homeBrandIconStyles, homeFontIconStyles, homeFontIconStylesMedium, iconDisplayStyle } from "../../src/taskpane/styles/iconStyles";
import { detailListStyles, detailsListClassNames } from "../Body/ContentBodyFilesFolders";
import { SharePointFolderContentTypeIds, initialFileOptionPanelInfoSetting, initialFolderOptionPanelInfoSetting } from "../../src/taskpane/config/constants";
import { IFilePanelInfo } from "../../src/taskpane/interfaces/IFilePanelInfo";
import FileOptionPanel from "../../src/taskpane/components/panels/FileOptionPanel";
import { FileTypeEnum } from "../../src/taskpane/components/enums/fileTypeEnum";
import FolderOptionPanel from "../../src/taskpane/components/panels/FolderOptionPanel";
import { IFolderPanelInfo } from "../../src/taskpane/interfaces/IFolderPanelInfo";
import { HomeItemType } from "./ContentManagerHome";
import { IStoreItem } from "../../src/taskpane/interfaces/IStoredItem";
import { AccessLicenseHelper } from "../../src/taskpane/components/helpers/accessLicenseHelper";
import { logger } from "../../utilities/logger";
import { ItemCheckFactory } from "../../src/taskpane/components/helpers/ItemCheck/ItemCheckFactory";
import { setOneDriveDetails, setWorkingDirectories } from "../../src/taskpane/redux/modules/workingDirectory";
import { getOneDriveRootDetails } from "../../src/taskpane/services/GraphService";
import generalModule from "../../utilities/GeneralModule";
import { setLoading } from "../../src/taskpane/redux/modules/loader";
import { setContentManagerSearchValue, setIsContentManagerContentLoading, toggleContentManagerReload } from "../../src/taskpane/redux/modules/contentManagerDirectory";
import { removeRecentItem } from "../../src/taskpane/redux/modules/recentItems";
import { getPinnedItems, removePinnedItem, updatePinnedItems } from "../../src/taskpane/redux/modules/pinnedItems";
import { setShowFolderDeleteDialog } from "../../src/taskpane/redux/modules/showFolderDeleteDialogue";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // import plugin
import { DraggableContainer } from "../../src/taskpane/components/_v2/components/Explorer/FolderView/DraggableContainer";
import { DragAndDropHelper, DragMode } from "../../utilities/DragAndDropHelper";
import { detailRowLinkStyles } from "../../src/taskpane/config/controlStyles";
import { FolderTypeEnum } from "../../src/taskpane/components/enums/FolderTypeEnum";
import { IOneDriveRootDetails } from "../../src/taskpane/interfaces/IOneDriveRootDetails";

dayjs.extend(utc);

const gridFileNameStackItemStyles: IStackItemStyles = {
    root: {
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap"
    }
}

const gridOptionButtonStackItemStyles: IStackItemStyles = {
    root: {
        height: "100%",
        marginTop: -8,
        marginBottom: -11
    }
}

interface ContentHomePinnedProps {
    selectedHomeFilterType: HomeItemType
}

const gridFileNameStackTokens: IStackTokens = {
    childrenGap: 5
};

function copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
    try {
        const key = columnKey as keyof T;
        return items.slice(0).sort((a: T, b: T) => {
            return ((isSortedDescending ? a[key] > b[key] : a[key] < b[key]) ? 1 : -1);
        });
    } catch (error) {
        console.error("copyAndSort: ", error);
        return items;
    }
};

export const ContentHomePinned = ({ selectedHomeFilterType }: ContentHomePinnedProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const iconNameResolver = new IconTypeNameResolver(languageStrings);

    const dispatch = useDispatch();

    const pinned = useSelector((state: RootState) => state.pinned);
    const user = useSelector((state: RootState) => state.user);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
    const isTessEnabled = useSelector((state: RootState) => state.tenantInfo?.isTessEnabled);

    const [fileOptionPanelInfo, setFileOptionPanelInfo] = React.useState<IFilePanelInfo>(initialFileOptionPanelInfoSetting);
    const [folderOptionPanelInfo, setFolderOptionPanelInfo] = React.useState<IFolderPanelInfo>(initialFolderOptionPanelInfoSetting);
    const [items, setItems] = React.useState<IStoreItem[]>([]);
    const [licenseMessageContent, setLicenseMessageContent] = React.useState<IDialogContentProps>(null);
    const [isLicenseDialogHidden, setIsLicenseDialogHidden] = React.useState(true);
    const [draggedItem, setDraggedItem] = React.useState<IStoreItem>();
    const [columns, setColumns] = React.useState<IColumn[]>();
    const columnClickedRef = React.useRef<{ key: string, isSorted: boolean, isSortedDescending: boolean }>();

    React.useEffect(() => {
        if (pinned && pinned.pinnedItems === null && user?.account?.accountId) {
            dispatch(getPinnedItems(user.account.accountId));
        }
    }, [pinned, user?.account?.accountId]);

    React.useEffect(() => {
        if (pinned && pinned.pinnedItems && selectedHomeFilterType) {
            if (selectedHomeFilterType.extension) {
                setItems(pinned.pinnedItems.filter(x => x.extension === selectedHomeFilterType.extension));
            } else if (selectedHomeFilterType.folderTypeIcon) {
                setItems(pinned.pinnedItems.filter(x => x.folderTypeIcon === selectedHomeFilterType.folderTypeIcon));
            } else {
                setItems(pinned.pinnedItems || [])
            }
        } else {
            setItems([]);
        }
    }, [pinned, selectedHomeFilterType]);

    const _onColumnClick = React.useCallback((_ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
        try {
            setColumns(prevCols => {
                const newColumns = prevCols.slice();
                const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];
                newColumns.forEach((newCol: IColumn) => {
                    if (newCol === currColumn) {
                        currColumn.isSortedDescending = !currColumn.isSortedDescending;
                        currColumn.isSorted = true;

                        columnClickedRef.current = {
                            key: currColumn.key,
                            isSorted: currColumn.isSorted,
                            isSortedDescending: currColumn.isSortedDescending
                        };
                    } else {
                        newCol.isSorted = false;
                        newCol.isSortedDescending = true;
                    }
                });

                setItems(prevFiltered => {
                    return copyAndSort(prevFiltered, currColumn.fieldName!, currColumn.isSortedDescending);
                });

                return newColumns;
            });
        } catch (error) {
            logger(error, "ContentHomePinned (onColumnClick)");
        }
    }, [items, columns]);

    React.useEffect(() => {
        setColumns([
            {
                key: "icon",
                name: "",
                minWidth: 40,
                maxWidth: 40,
                isResizable: true,
                onRender(item, index, column) {
                    if (item.fileType === FileTypeEnum.file) {
                        return <Stack horizontal verticalAlign="center" tokens={gridFileNameStackTokens}>
                            <Stack.Item>
                                <FontIcon iconName="GripperBarVertical" className={detailsListClassNames.gripperIcon} style={{ fontSize: 16 }} />
                            </Stack.Item>
                            <Stack.Item>
                                <Icon {...getFileTypeIconProps({ extension: item.extension, size: 20, imageFileType: "svg" })} styles={iconDisplayStyle} />
                            </Stack.Item>
                        </Stack>
                    } else {
                        const folderIcon = FolderTypeIconQuery.getImage(item.folderTypeIcon);
                        return <Stack horizontal verticalAlign="center" tokens={gridFileNameStackTokens}>
                            <Stack.Item>
                                <FontIcon iconName="GripperBarVertical" className={detailsListClassNames.gripperIcon} style={{ fontSize: 16 }} />
                            </Stack.Item>
                            <Stack.Item>
                                {folderIcon.imageType === ImageTypeEnum.fileType || item.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)
                                    ? <Icon {...getFileTypeIconProps({ type: item.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet) ? FileIconType.docset : folderIcon.fileIconType, size: 20, imageFileType: "svg" })} styles={iconDisplayStyle} />
                                    : folderIcon.imageType === ImageTypeEnum.url
                                        ? <img src={folderIcon.location} className={homeBrandIconStyles} width={20} height={20} />
                                        : folderIcon.svgComponent}
                            </Stack.Item>
                        </Stack>
                    }
                },
            },
            {
                key: "name",
                name: languageStrings.Name,
                isSorted: columnClickedRef.current && columnClickedRef.current.key === "name" ? columnClickedRef.current.isSorted : false,
                isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "name" ? columnClickedRef.current.isSortedDescending : false,
                onColumnClick: _onColumnClick,
                fieldName: "name",
                minWidth: 150,
                maxWidth: 400,
                isRowHeader: true,
                isResizable: true,
                onRender(item: IStoreItem) {
                    return (
                        <Stack horizontal verticalAlign="center" tokens={gridFileNameStackTokens}>
                            <Stack.Item grow styles={gridFileNameStackItemStyles}>
                                <Link
                                    data-selection-disabled={true}
                                    rel="noopener"
                                    styles={detailRowLinkStyles}
                                    onClick={() => {
                                        if (item.fileType === FileTypeEnum.folder) {
                                            browseLocation(item, false);
                                        } else {
                                            const options = {
                                                driveId: item.driveId,
                                                fileType: item.extension,
                                                name: item.name,
                                                lastModifiedDateTime: item.lastModifiedDateTime,
                                                isOpen: true,
                                                itemId: item.itemId,
                                                folderId: item.folderId,
                                                isRecentItem: false,
                                                filePath: item.filePath,
                                                modifiedBy: item.modifiedBy,
                                                isFromHomePage: true,
                                                isFromPinnedItems: true,
                                                cannotbeRemoved: item?.cannotBeRemoved
                                            };
                                            setFileOptionPanelInfo(options);
                                        }
                                    }}
                                >
                                    <TooltipHost content={item.name} overflowMode={TooltipOverflowMode.Parent}>
                                        <span aria-describedby={item.name}>{item.name}</span>
                                    </TooltipHost>
                                </Link>
                            </Stack.Item>
                            <Stack.Item styles={gridOptionButtonStackItemStyles}>
                                <TooltipHost content={languageStrings.MoreActions}>
                                    <IconButton
                                        aria-label="Options"
                                        className={detailsListClassNames.optionButton}
                                        iconProps={{ iconName: 'MoreVertical' }}
                                        onClick={() => {
                                            if (item.fileType === FileTypeEnum.file) {
                                                const options = {
                                                    driveId: item.driveId,
                                                    fileType: item.extension,
                                                    name: item.name,
                                                    lastModifiedDateTime: item.lastModifiedDateTime,
                                                    isOpen: true,
                                                    itemId: item.itemId,
                                                    folderId: item.folderId,
                                                    isRecentItem: false,
                                                    filePath: item.filePath,
                                                    modifiedBy: item.modifiedBy,
                                                    isFromHomePage: true,
                                                    isFromPinnedItems: true,
                                                    cannotbeRemoved: item?.cannotBeRemoved
                                                };
                                                setFileOptionPanelInfo(options);
                                            } else {
                                                const folderType: FolderTypeEnum = FolderTypeIconQuery.getFolderType(item.folderTypeIcon);
                                                const options: IFolderPanelInfo = {
                                                    isOpen: true,
                                                    folder: {
                                                        driveId: item.driveId,
                                                        id: item.itemId,
                                                        name: item.name,
                                                        sharePointListId: item.sharePointListId,
                                                        sharePointUniqueId: item.sharePointUniqueId,
                                                        isSharedTeam: item.isSharedTeam,
                                                        isList: item.isList || folderType === FolderTypeEnum.sharepointList
                                                    },
                                                    locations: item.locations,
                                                    siteId: item.siteId,
                                                    webUrl: item.webUrl,
                                                    //saveToOneDrive: saveToOneDrive,
                                                    folderType,
                                                    isARecentItem: false,
                                                    isFromHomePage: true,
                                                    isFromPinnedItems: true,
                                                    isTeamsSiteInsideSharepoint: item.isTeamsSiteInsideSharepoint,
                                                    tessAccountId: item.tessAccountId,
                                                    tessProjectId: item.tessProjectId
                                                }
                                                setFolderOptionPanelInfo(options);
                                            }

                                        }}
                                    />
                                </TooltipHost>
                            </Stack.Item>
                        </Stack>
                    )
                }
            },
            {
                key: "date",
                name: languageStrings.Date,
                fieldName: "datePinned",
                minWidth: 150,
                maxWidth: 250,
                isRowHeader: true,
                isResizable: true, //getTimeZoneFormattedDate(item[colName], "L LT")
                onRender(item: IStoreItem) {
                    return item.datePinned ? dayjs.utc(item.datePinned.toString()).local().format("L LT") : "-";
                },
                isSorted: columnClickedRef.current && columnClickedRef.current.key === "date" ? columnClickedRef.current.isSorted : false,
                isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "date" ? columnClickedRef.current.isSortedDescending : false,
                onColumnClick: _onColumnClick,
            },
            {
                key: "location",
                name: languageStrings.Location,
                fieldName: "location",
                minWidth: 150,
                isRowHeader: true,
                isResizable: true,
                onRender(item) {
                    return getPath(item.locations, item.folderTypeIcon, true) || item.filePath;
                },
                isSorted: columnClickedRef.current && columnClickedRef.current.key === "location" ? columnClickedRef.current.isSorted : false,
                isSortedDescending: columnClickedRef.current && columnClickedRef.current.key === "location" ? columnClickedRef.current.isSortedDescending : false,
                onColumnClick: _onColumnClick,
            }
        ]);
    }, []);

    const hideLicenseDialog = () => {
        setIsLicenseDialogHidden(true);
    }

    const showLicenseDialog = () => {
        setIsLicenseDialogHidden(false);
    }

    const browseLocation = async (homeItem: IStoreItem, isRecentList: boolean) => {
        try {
            if (await IsFolderInValidState(homeItem, isRecentList)) {
                setContextAwareLoading(true);

                let oneDriveDetails: IOneDriveRootDetails;
                const isOneDriveItem: boolean = homeItem.folderTypeIcon === FolderTypeIconEnum.oneDrive || homeItem.folderTypeIcon === FolderTypeIconEnum.oneDriveSubFolder;
                if (isOneDriveItem) {
                    if (!oneDriveRootDetails) {
                        oneDriveDetails = await getOneDriveRootDetails();
                        dispatch(setOneDriveDetails(oneDriveDetails));
                    }
                    else {
                        oneDriveDetails = oneDriveRootDetails;
                    }
                }

                if (!generalModule.isInConentMangerMode()) {
                    setContextAwareLoading(false);
                }

                if (Array.isArray(homeItem.locations) && homeItem.locations.length > 0) {
                    if (isOneDriveItem && oneDriveDetails?.sharePointListId) {
                        const homeItemLocations: IPageSettings[] = homeItem.locations.map(location => ({
                            ...location,
                            sharePointListId: oneDriveDetails.sharePointListId
                        }));
                        dispatch(setWorkingDirectories(homeItemLocations));
                    }
                    else {
                        dispatch(setWorkingDirectories(homeItem.locations));
                    }
                }

                if (generalModule.isInConentMangerMode()) {
                    dispatch(setContentManagerSearchValue(""));
                    dispatch(toggleContentManagerReload());
                    setContextAwareLoading(false);
                }
            }
        } catch (error) {
            setContextAwareLoading(false);
            logger(error, "ContentHomePinned.tsx (browseLocation)");
        }
    }

    const IsFolderInValidState = async (item: IStoreItem, isRecentList: boolean) => {
        if (isTessEnabled && (item.tessAccountId || item.tessProjectId)) {
            return true;
        }

        let returnValue = true;
        try {

            let locationType = item.locations[0]?.type;

            if (!AccessLicenseHelper.canAccessItem(user.licenseType, locationType)) {
                const errorMessage = {
                    type: DialogType.largeHeader,
                    title: languageStrings.LicenseRequiredTitle,
                    closeButtonAriaLabel: "Close",
                    subText: languageStrings.LicenseRequired.replace("{placeholder}", locationType)
                };
                setLicenseMessageContent(errorMessage);
                showLicenseDialog();
                returnValue = false;
                return returnValue;
            }

            // if (locationType !== ComponentTypes.Docubird && (!user.settings.enableOneDrive || !user.settings.enableSharePoint || !user.settings.enableTeams)) {
            //     if (!(locationType === ComponentTypes.OneDrive || locationType === ComponentTypes.OneDriveSubFolder)) {
            //         let isTeams = false;
            //         item.locations?.every(loc => {
            //             if (loc.teamGroupId || loc.isTeamsRootFolder || loc.isTeamsFolder) {
            //                 isTeams = true;
            //             }
            //             return !isTeams;
            //         })
            //         locationType = isTeams ? ComponentTypes.Teams : locationType === ComponentTypes.SharePointList.toString() ? ComponentTypes.SharePointList : ComponentTypes.SharePoint;
            //     }
            //     const { canAccess, type } = AccessLicenseHelper.isLocationEnabled(locationType, user.settings);
            //     if (!canAccess) {
            //         const errorMessage = {
            //             type: DialogType.largeHeader,
            //             title: languageStrings.AccessDenied,
            //             closeButtonAriaLabel: "Close",
            //             subText: languageStrings.AccessRevoked.replace("{placeholder}", type)
            //         };
            //         setLicenseMessageContent(errorMessage);
            //         showLicenseDialog();
            //         returnValue = false;
            //         return returnValue;
            //     }
            // }

            if (!(await CheckIfFolderExists(item))) {
                doFolderRemovalWork(isRecentList, item);
                returnValue = false;
            }
        }
        catch (e) {
            logger(e, "ContentHomePinned.tsx (isFolderInValidState)");
        }

        return returnValue;
    };

    const doFolderRemovalWork = (isRecentList: boolean, item: IStoreItem) => {
        const itemToRemove = { itemId: item.itemId, driveId: item.driveId };
        isRecentList
            ? dispatch(removeRecentItem(user.account.accountId, itemToRemove, FileTypeEnum.folder))
            : dispatch(removePinnedItem(user.account.accountId, itemToRemove, FileTypeEnum.folder));
        dispatch(setShowFolderDeleteDialog(true));
    };

    const CheckIfFolderExists = async (item: IStoreItem) => {
        const factory = new ItemCheckFactory(item.driveId, item.sharePointListId, item.siteId);
        const folderType = FolderTypeIconQuery.getFolderType(item.folderTypeIcon);
        const itemChecker = factory.createItemChecker(folderType, item?.isTeamsSiteInsideSharepoint);
        return await itemChecker.doesItemExist(item.itemId);
    };

    const setContextAwareLoading = React.useCallback((isLoading: boolean, contentManagerOnly: boolean = false) => {
        if (!contentManagerOnly) {
            !generalModule.isInConentMangerMode() ? dispatch(setLoading(isLoading)) : dispatch(setIsContentManagerContentLoading(isLoading));
        }
        else {
            dispatch(setIsContentManagerContentLoading(isLoading));
        }
    }, []);

    const licenseDialog = (
        <Dialog hidden={isLicenseDialogHidden} onDismiss={hideLicenseDialog} dialogContentProps={licenseMessageContent}>
            <DialogFooter>
                <PrimaryButton autoFocus onClick={hideLicenseDialog} text="Ok" />
            </DialogFooter>
        </Dialog>
    );

    const defaultShimmerColumns: IColumn[] = React.useMemo(() => {
        const columns: IColumn[] = [
            { key: "0", name: "", minWidth: 16, maxWidth: 16 },
            { key: "1", name: languageStrings.Name, minWidth: 200 },
            { key: "2", name: languageStrings.Modified, minWidth: 150 },
            { key: "3", name: languageStrings.Size, minWidth: 150 },
            { key: "4", name: languageStrings.ModifiedBy, minWidth: 150 }];
        return columns;
    }, []);

    const getPath = (itemLocations: IPageSettings[], folderTypeIcon: FolderTypeIconEnum, includeBrackets = true): string => {
        try {
            const type = iconNameResolver.getIconTypeName(folderTypeIcon);
            const path = itemLocations
                .filter(dir => dir.type !== ComponentTypes.Saving)
                .map(dir => dir?.displayName)
                .join("/");

            return includeBrackets ? `[${type}] ${path}` : path;
        } catch (error) {
            return ""
        }
    };

    const getFontIconClassName = (fontIcon): string => {
        if (fontIcon.imageSize === ImageSizeEnum.medium) {
            return homeFontIconStylesMedium;
        }
        return homeFontIconStyles;
    }

    const _onRenderRow: IDetailsListProps['onRenderRow'] = rowProps => {
        return (
            <DraggableContainer
                isValidDropZone={rowProps.item.fileType === FileTypeEnum.folder}
                onDropAfterFiles={(selectedGridItems?: any[]) => onGridItemDrop(rowProps.item, true, selectedGridItems)}
                onDragHover={() => onGridItemDrop(rowProps.item, false)}
                gridItem={rowProps.item}
            >
                <DetailsRow {...rowProps} className={detailsListClassNames.row} />
            </DraggableContainer>
        )
    }

    const dismissFileOptionPanel = React.useCallback(() => {
        setFileOptionPanelInfo(initialFileOptionPanelInfoSetting);
    }, []);

    const dismissFolderOptionPanel = React.useCallback(() => {
        setFolderOptionPanelInfo(initialFolderOptionPanelInfoSetting);
    }, []);

    const onGridItemDrop = React.useCallback(async (item, hasDropContent: boolean = true, selectedGridItems?: any[]) => {
        try {
            if (DragAndDropHelper.mode === DragMode.files || !hasDropContent) {
                if (hasDropContent && DragAndDropHelper.mode === DragMode.files) {
                    generalModule.setOpenPanelUploadPanel(true);
                }
                dispatch(setWorkingDirectories(item.locations));
                dispatch(setContentManagerSearchValue(""));
                dispatch(toggleContentManagerReload());
            }
        } catch (error) {
            logger(error, "ContentHomePinned.tsx (onGridItemDrop)");
        }
    }, [oneDriveRootDetails]);

    const insertBeforeItem = (item: IStoreItem) => {
        const insertIndex = items.indexOf(item);
        const filteredItems = items.filter(stateItem => stateItem !== draggedItem);
        filteredItems.splice(insertIndex, 0, draggedItem);
        dispatch(updatePinnedItems(user.account.accountId, filteredItems));
        setItems(filteredItems);
    }

    const dragDropEvents = () => {
        return {
            canDrop: (dropContext?: IDragDropContext, dragContext?: IDragDropContext) => {
                return true;
            },
            canDrag: (item?: any) => {
                return true;
            },
            onDragLeave: (item?: any, event?: DragEvent) => {
                return;
            },
            onDrop: (item?: any, event?: DragEvent) => {
                if (draggedItem) {
                    insertBeforeItem(item);
                }
            },
            onDragStart: (item?: any, itemIndex?: number, selectedItems?: any[], event?: MouseEvent) => {
                setDraggedItem(item);
            },
            onDragEnd: (item?: any, event?: DragEvent) => {
                setDraggedItem(undefined);
            },
        };
    }

    return (
        <>
            <ShimmeredDetailsList
                styles={detailListStyles}
                compact={false}
                onRenderRow={_onRenderRow}
                items={items}
                columns={columns?.length > 0 ? columns : defaultShimmerColumns}
                selectionMode={SelectionMode.none}
                constrainMode={ConstrainMode.unconstrained}
                setKey="multiple"
                enableShimmer={pinned.isPinnedItemsLoading}
                layoutMode={DetailsListLayoutMode.justified}
                ariaLabelForSelectionColumn="Toggle selection"
                ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                checkButtonAriaLabel="Row checkbox"
                className="xb-search-grid"
                dragDropEvents={dragDropEvents()}
            />
            {fileOptionPanelInfo?.isOpen && <FileOptionPanel info={fileOptionPanelInfo} dismissPanel={dismissFileOptionPanel} />}
            {folderOptionPanelInfo?.isOpen && <FolderOptionPanel info={folderOptionPanelInfo} dismissPanel={dismissFolderOptionPanel} />}
            {licenseDialog}
        </>
    )
};